import React, { useState, useEffect } from "react";
import { X, Download, AlertCircle, Loader } from "lucide-react";
import { supabase } from "../lib/supabase";

interface DownloadSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (downloadHtml: string) => void;
}

interface Upload {
  id: string;
  name: string;
  url: string;
  size: number;
  file_type: string;
  description?: string;
  created_at: string;
}

export default function DownloadSelectorModal({
  isOpen,
  onClose,
  onSelect,
}: DownloadSelectorModalProps) {
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUpload, setSelectedUpload] = useState<Upload | null>(null);
  const [formData, setFormData] = useState({
    title: "Free Resource",
    description:
      "Get our comprehensive resource to help you implement these strategies effectively.",
    buttonText: "Download PDF",
    source: "Blog Download",
  });

  useEffect(() => {
    if (isOpen) {
      fetchUploads();
    } else {
      setSelectedUpload(null);
      setFormData({
        title: "Free Resource",
        description:
          "Get our comprehensive resource to help you implement these strategies effectively.",
        buttonText: "Download PDF",
        source: "Blog Download",
      });
    }
  }, [isOpen]);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("uploads")
        .select("*")
        .eq("deleted", false)
        .eq("file_type", "application/pdf")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setUploads(data || []);
    } catch (err) {
      console.error("Error fetching uploads:", err);
      setError("Failed to load PDF files");
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = () => {
    if (!selectedUpload) {
      setError("Please select a PDF file");
      return;
    }

    // Generate the HTML for the download section
    const downloadHtml = `
<div id="blog-download-section" 
     data-pdf-name="${selectedUpload.name}"
     data-title="${formData.title}"
     data-description="${formData.description}"
     data-button-text="${formData.buttonText}"
     data-source="${formData.source}">
  <!-- This div will be replaced with the download button by BlogPost component -->
  Download Button Placeholder
</div>`;

    onSelect(downloadHtml);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Insert Download Section
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">
                Download Section Settings
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Section Title
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) =>
                      setFormData({ ...formData, buttonText: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Source (for tracking)
                  </label>
                  <input
                    type="text"
                    value={formData.source}
                    onChange={(e) =>
                      setFormData({ ...formData, source: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring-[#F98B3D]"
                  />
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Preview
                </h4>
                <div className="p-4 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {formData.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {formData.description}
                  </p>
                  <div className="inline-flex items-center px-3 py-2 bg-[#F98B3D] text-white rounded-md">
                    <Download className="h-4 w-4 mr-2" />
                    {formData.buttonText}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">
                Select PDF File
              </h3>
              {loading ? (
                <div className="flex justify-center items-center h-32">
                  <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
                </div>
              ) : uploads.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                  <Download className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">No PDF files found</p>
                  <p className="text-sm text-gray-500">
                    Upload PDF files in the Admin &gt; Uploads section
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                  {uploads.map((upload) => (
                    <div
                      key={upload.id}
                      onClick={() => setSelectedUpload(upload)}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                        selectedUpload?.id === upload.id
                          ? "border-[#F98B3D] bg-orange-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <Download
                        className={`h-5 w-5 mr-3 ${
                          selectedUpload?.id === upload.id
                            ? "text-[#F98B3D]"
                            : "text-gray-400"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium truncate ${
                            selectedUpload?.id === upload.id
                              ? "text-[#F98B3D]"
                              : "text-gray-900"
                          }`}
                        >
                          {upload.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(upload.size)} •{" "}
                          {new Date(upload.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="mr-3 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleInsert}
            disabled={!selectedUpload || loading}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              !selectedUpload || loading
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#F98B3D] text-white hover:bg-[#e07a2c]"
            }`}
          >
            Insert Download Section
          </button>
        </div>
      </div>
    </div>
  );
}
